import { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, Pressable, TextInput, ActivityIndicator, Modal, TouchableWithoutFeedback, Platform, ScrollView } from 'react-native';
import * as DocumentPicker from 'expo-document-picker';
import { Picker } from '@react-native-picker/picker';
import { Upload, FileText, Check, X, Edit, AlertCircle, ChevronDown, ChevronUp, Search } from '../../lib/icons';
import { supabase } from '../../lib/supabase';
import { PropertyRecordWithState, Home } from './types';

// Extended Home type with owner information
interface HomeWithOwner extends Home {
  owner_name?: string;
}

interface ParsedPropertyImportData {
  property_records: Record<string, any>[];
  property_address?: string;
}
import { shadows } from '../../lib/styles';
import WebScrollView from '../../components/WebScrollView';

const PropertyImport = () => {
  const [homes, setHomes] = useState<HomeWithOwner[]>([]);
  const [selectedHome, setSelectedHome] = useState<string>('');
  const [homeSearchQuery, setHomeSearchQuery] = useState<string>('');
  const [showHomeDropdown, setShowHomeDropdown] = useState(false);
  const [jsonFile, setJsonFile] = useState<{ name: string; data: any } | null>(null);
  const [pdfFile, setPdfFile] = useState<{ name: string; uri: string } | null>(null);
  const [jsonPaste, setJsonPaste] = useState<string>('');
  const [inputMode, setInputMode] = useState<'upload' | 'paste' | 'pdf'>('upload');
  const [pdfParseStatus, setPdfParseStatus] = useState<'idle' | 'extracting' | 'parsing'>('idle');
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [records, setRecords] = useState<PropertyRecordWithState[]>([]);
  const [editingRecord, setEditingRecord] = useState<PropertyRecordWithState | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [importProgress, setImportProgress] = useState<{ current: number; total: number } | null>(null);
  const typeaheadRef = useRef<View>(null);
  const parseButtonDisabled =
    (inputMode === 'upload' && !jsonFile) ||
    (inputMode === 'paste' && !jsonPaste.trim()) ||
    (inputMode === 'pdf' && !pdfFile) ||
    loading;

  useEffect(() => {
    fetchHomes();
  }, []);

  useEffect(() => {
    if (Platform.OS === 'web' && showHomeDropdown) {
      const handleClickOutside = (event: MouseEvent) => {
        if (typeaheadRef.current && !(typeaheadRef.current as any).contains(event.target)) {
          setShowHomeDropdown(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showHomeDropdown]);

  const fetchHomes = async () => {
    try {
      const { data, error } = await supabase
        .from('homes')
        .select(`
          id,
          address,
          city,
          state,
          zip_code,
          square_footage,
          year_built,
          cover_photo_url,
          lot_size,
          bedrooms,
          bathrooms,
          home_notes,
          owner_id,
          created_at,
          profiles!homes_owner_id_fkey (
            full_name
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Transform the data to include owner_name
      const homesWithOwner = (data || []).map((home: any) => ({
        ...home,
        owner_name: home.profiles?.full_name || 'Unknown Owner'
      }));
      
      setHomes(homesWithOwner);
    } catch (error: any) {
      console.error('Error fetching homes:', error);
      setError('Failed to fetch homes');
    }
  };

  const handlePickDocument = async () => {
    try {
      setError(null);
      const result = await DocumentPicker.getDocumentAsync({
        type: 'application/json',
        copyToCacheDirectory: true,
      });

      if (result.canceled) {
        return;
      }

      const file = result.assets[0];
      
      // For web, we can read the file directly
      if (Platform.OS === 'web' && file.uri) {
        const response = await fetch(file.uri);
        const jsonData = await response.json();
        
        // Validate JSON structure
        if (!jsonData.property_records || !Array.isArray(jsonData.property_records)) {
          throw new Error('Invalid JSON format: missing or invalid "property_records" array');
        }
        
        setJsonFile({
          name: file.name,
          data: jsonData
        });
      } else {
        // For native, read file content
        if (file.uri) {
          const response = await fetch(file.uri);
          const jsonData = await response.json();
          
          if (!jsonData.property_records || !Array.isArray(jsonData.property_records)) {
            throw new Error('Invalid JSON format: missing or invalid "property_records" array');
          }
          
          setJsonFile({
            name: file.name,
            data: jsonData
          });
        }
      }
    } catch (error: any) {
      console.error('Error picking document:', error);
      setError(error.message || 'Failed to pick document');
    }
  };

  const handlePickPDF = async () => {
    try {
      setError(null);
      const result = await DocumentPicker.getDocumentAsync({
        type: 'application/pdf',
        copyToCacheDirectory: true,
      });

      if (result.canceled) {
        return;
      }

      const file = result.assets[0];
      if (!file?.uri) {
        throw new Error('Unable to access selected PDF file');
      }

      setPdfFile({
        name: file.name,
        uri: file.uri,
      });
      setRecords([]);
    } catch (error: any) {
      console.error('Error picking PDF:', error);
      setError(error.message || 'Failed to pick PDF');
    }
  };

  const encodePdfToDataUrl = async (fileUri: string): Promise<string> => {
    const response = await fetch(fileUri);
    if (!response.ok) {
      throw new Error('Failed to read selected PDF');
    }

    const arrayBuffer = await response.arrayBuffer();
    const fileSizeMb = arrayBuffer.byteLength / (1024 * 1024);
    if (fileSizeMb > 50) {
      throw new Error('PDF must be under 50MB');
    }

    if (typeof btoa !== 'function') {
      throw new Error('PDF parsing is currently supported on web only.');
    }

    // Build base64 in chunks to avoid stack overflows on larger files.
    const bytes = new Uint8Array(arrayBuffer);
    const chunkSize = 0x8000;
    let binary = '';

    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.subarray(i, i + chunkSize);
      binary += String.fromCharCode(...chunk);
    }

    return `data:application/pdf;base64,${btoa(binary)}`;
  };

  const handleLoadJSON = async () => {
    if (!selectedHome) {
      setError('Please select a home');
      return;
    }

    let jsonData: any;

    try {
      setLoading(true);
      setError(null);
      setRecords([]);

      // Get JSON data from either file or paste
      if (inputMode === 'upload') {
        if (!jsonFile) {
          setError('Please upload a JSON file or switch to paste mode');
          return;
        }
        jsonData = jsonFile.data;
      } else {
        if (!jsonPaste.trim()) {
          setError('Please paste JSON data or switch to upload mode');
          return;
        }
        // Parse pasted JSON
        jsonData = JSON.parse(jsonPaste);
      }

      // Validate JSON structure
      if (!jsonData.property_records || !Array.isArray(jsonData.property_records)) {
        throw new Error('Invalid JSON format: missing or invalid "property_records" array');
      }

      // Convert to PropertyRecordWithState and update home_id
      const recordsWithState: PropertyRecordWithState[] = jsonData.property_records.map((record: any, index: number) => ({
        ...record,
        home_id: selectedHome, // Override with selected home
        tempId: `temp-${Date.now()}-${index}`,
        state: 'accepted' as const
      }));

      setRecords(recordsWithState);
      setSuccess(`Successfully loaded ${recordsWithState.length} records from JSON`);
      setTimeout(() => setSuccess(null), 5000);
    } catch (error: any) {
      console.error('Error loading JSON:', error);
      if (error instanceof SyntaxError) {
        setError('Invalid JSON format. Please check your JSON syntax.');
      } else {
        setError(error.message || 'Failed to load JSON');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleParsePDF = async () => {
    if (!selectedHome) {
      setError('Please select a home');
      return;
    }

    if (!pdfFile) {
      setError('Please upload a PDF file');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      setRecords([]);
      setPdfParseStatus('extracting');
      const pdfDataUrl = await encodePdfToDataUrl(pdfFile.uri);

      setPdfParseStatus('parsing');
      const { data, error: parseError } = await supabase.functions.invoke('parse-property-pdf', {
        body: {
          pdfBase64: pdfDataUrl,
          fileName: pdfFile.name || 'ownerly-report.pdf',
          homeId: selectedHome,
        },
      });

      if (parseError) {
        throw parseError;
      }

      if (!data?.property_records || !Array.isArray(data.property_records)) {
        throw new Error('AI parsing returned an invalid response format.');
      }

      const parsedData = data as ParsedPropertyImportData;
      const recordsWithState: PropertyRecordWithState[] = parsedData.property_records.map((record: any, index: number) => ({
        ...record,
        home_id: selectedHome,
        tempId: `temp-${Date.now()}-${index}`,
        state: 'accepted' as const,
      }));

      setRecords(recordsWithState);
      setSuccess(`Successfully parsed ${recordsWithState.length} records from PDF`);
      setTimeout(() => setSuccess(null), 5000);
    } catch (error: any) {
      console.error('Error parsing PDF:', error);
      setError(error.message || 'Failed to parse PDF');
    } finally {
      setPdfParseStatus('idle');
      setLoading(false);
    }
  };

  const handleToggleRecordState = (tempId: string) => {
    setRecords(prev => prev.map(record => {
      if (record.tempId === tempId) {
        return {
          ...record,
          state: record.state === 'accepted' ? 'skipped' : 'accepted'
        };
      }
      return record;
    }));
  };

  const handleEditRecord = (record: PropertyRecordWithState) => {
    setEditingRecord({ ...record });
    setShowEditModal(true);
  };

  const handleSaveEdit = () => {
    if (!editingRecord) return;

    setRecords(prev => prev.map(record => 
      record.tempId === editingRecord.tempId ? editingRecord : record
    ));
    setShowEditModal(false);
    setEditingRecord(null);
  };

  const handleAcceptAll = () => {
    setRecords(prev => prev.map(record => ({ ...record, state: 'accepted' as const })));
  };

  const handleSkipAll = () => {
    setRecords(prev => prev.map(record => ({ ...record, state: 'skipped' as const })));
  };

  const handleImportRecords = async () => {
    const acceptedRecords = records.filter(r => r.state === 'accepted');
    
    if (acceptedRecords.length === 0) {
      setError('No records selected for import');
      return;
    }

    try {
      setImporting(true);
      setError(null);
      setImportProgress({ current: 0, total: acceptedRecords.length });

      let imported = 0;
      let skipped = 0;
      const errors: string[] = [];

      for (let i = 0; i < acceptedRecords.length; i++) {
        const record = acceptedRecords[i];
        setImportProgress({ current: i + 1, total: acceptedRecords.length });

        try {
          // Validate required fields
          if (!record.date) {
            console.log(`Skipping record "${record.title}": missing required date field`);
            errors.push(`${record.title}: Missing required date field`);
            continue;
          }

          if (!record.title || !record.record_type) {
            console.log(`Skipping record "${record.title}": missing required fields`);
            errors.push(`${record.title || 'Untitled'}: Missing required fields (title or record_type)`);
            continue;
          }

          // Check for duplicates (use .maybeSingle() to avoid errors when no record found)
          const { data: existing, error: checkError } = await supabase
            .from('property_records')
            .select('id')
            .eq('home_id', record.home_id)
            .eq('title', record.title)
            .eq('date', record.date)
            .maybeSingle();

          // If there's an error checking (like 406), log it but continue - might be a special character issue
          if (checkError && checkError.code !== 'PGRST116') {
            console.warn(`Error checking duplicates for "${record.title}":`, checkError);
            // Continue anyway - will try to insert and let database handle duplicates
          }

          if (existing) {
            console.log(`Skipping duplicate record: ${record.title}`);
            skipped++;
            continue;
          }

          // Insert the record
          const { error: insertError } = await supabase
            .from('property_records')
            .insert({
              home_id: record.home_id,
              record_type: record.record_type,
              title: record.title,
              description: record.description || null,
              date: record.date,
              amount: record.amount || null,
              url: record.url || null,
              metadata: record.metadata || {}
            });

          if (insertError) {
            console.error(`Failed to insert record "${record.title}":`, insertError);
            errors.push(`${record.title}: ${insertError.message}`);
          } else {
            imported++;
          }
        } catch (error: any) {
          console.error(`Error processing record "${record.title}":`, error);
          errors.push(`${record.title}: ${error.message}`);
        }
      }

      setImportProgress(null);

      // Show results
      let message = `Successfully imported ${imported} record(s)`;
      if (skipped > 0) {
        message += `, skipped ${skipped} duplicate(s)`;
      }
      if (errors.length > 0) {
        message += `. ${errors.length} error(s) occurred.`;
        setError(`Import completed with errors:\n${errors.join('\n')}`);
      }
      
      setSuccess(message);
      
      // Clear records after successful import
      if (imported > 0) {
        setRecords([]);
        setJsonFile(null);
        setJsonPaste('');
      }
    } catch (error: any) {
      console.error('Error importing records:', error);
      setError(error.message || 'Failed to import records');
    } finally {
      setImporting(false);
    }
  };

  const acceptedCount = records.filter(r => r.state === 'accepted').length;
  const skippedCount = records.filter(r => r.state === 'skipped').length;

  const getHomeDisplayText = (home: HomeWithOwner) => {
    const address = `${home.address}, ${home.city}, ${home.state}`;
    const ownerName = home.owner_name || 'Unknown Owner';
    return `${address} - ${ownerName} (${home.id})`;
  };

  const getSelectedHomeDisplayText = () => {
    if (!selectedHome) return '';
    const home = homes.find(h => h.id === selectedHome);
    return home ? getHomeDisplayText(home) : '';
  };

  const filteredHomes = homes.filter(home => {
    if (!homeSearchQuery.trim()) return true;
    const searchLower = homeSearchQuery.toLowerCase();
    const address = `${home.address}, ${home.city}, ${home.state}`.toLowerCase();
    const ownerName = (home.owner_name || 'Unknown Owner').toLowerCase();
    const homeId = home.id.toLowerCase();
    return address.includes(searchLower) || ownerName.includes(searchLower) || homeId.includes(searchLower);
  });

  const handleHomeSelect = (homeId: string) => {
    const home = homes.find(h => h.id === homeId);
    setSelectedHome(homeId);
    setHomeSearchQuery(home ? getHomeDisplayText(home) : '');
    setShowHomeDropdown(false);
  };

  const handleHomeSearchChange = (text: string) => {
    setHomeSearchQuery(text);
    setShowHomeDropdown(true);
    if (selectedHome && text !== getSelectedHomeDisplayText()) {
      setSelectedHome('');
    }
  };

  const getRecordTypeBadgeStyle = (type: string) => {
    switch (type) {
      case 'permit':
        return styles.permitBadge;
      case 'sale':
        return styles.saleBadge;
      case 'tax_assessment':
        return styles.taxBadge;
      case 'lien':
        return styles.lienBadge;
      default:
        return styles.otherBadge;
    }
  };

  return (
    <View style={styles.container}>
      {/* Upload Section */}
      <View style={[
        styles.card,
        Platform.OS === 'web' && { overflow: 'visible' as any }
      ]}>
        <View style={styles.cardHeader}>
          <Upload size={24} color="#9ca3af" />
          <Text style={styles.cardTitle}>Upload Property Records JSON</Text>
        </View>

        <View style={[
          styles.formGroup,
          Platform.OS === 'web' && { overflow: 'visible' as any, zIndex: 1 }
        ]}>
          <Text style={styles.label}>Select Home</Text>
          <View ref={typeaheadRef} style={[
            styles.typeaheadContainer,
            showHomeDropdown && styles.typeaheadContainerOpen
          ]}>
            <View style={styles.typeaheadInputContainer}>
              <Search size={20} color="#6b7280" />
              <TextInput
                style={styles.typeaheadInput}
                value={homeSearchQuery}
                onChangeText={handleHomeSearchChange}
                onFocus={() => {
                  setShowHomeDropdown(true);
                }}
                placeholder="Search for a home..."
                placeholderTextColor="#9ca3af"
              />
              {(selectedHome || homeSearchQuery.trim()) && (
                <Pressable
                  onPress={() => {
                    setSelectedHome('');
                    setHomeSearchQuery('');
                    setShowHomeDropdown(false);
                  }}
                  style={styles.clearButton}
                >
                  <X size={16} color="#6b7280" />
                </Pressable>
              )}
              <Pressable
                onPress={() => setShowHomeDropdown(!showHomeDropdown)}
                style={styles.dropdownToggle}
              >
                {showHomeDropdown ? (
                  <ChevronUp size={20} color="#6b7280" />
                ) : (
                  <ChevronDown size={20} color="#6b7280" />
                )}
              </Pressable>
            </View>
            {showHomeDropdown && filteredHomes.length > 0 && (
              <View style={styles.typeaheadDropdown}>
                <WebScrollView style={styles.dropdownScrollView}>
                  {filteredHomes.map((home) => (
                    <Pressable
                      key={home.id}
                      style={[
                        styles.dropdownItem,
                        selectedHome === home.id && styles.dropdownItemSelected
                      ]}
                      onPress={() => handleHomeSelect(home.id)}
                    >
                      <View style={styles.dropdownItemContent}>
                        <Text style={styles.dropdownItemText}>
                          {home.address}, {home.city}, {home.state}
                        </Text>
                        <View style={styles.dropdownItemMeta}>
                          <Text style={styles.dropdownItemOwner}>
                            {home.owner_name || 'Unknown Owner'}
                          </Text>
                          <Text style={styles.dropdownItemId}>
                            {home.id}
                          </Text>
                        </View>
                      </View>
                      {selectedHome === home.id && (
                        <Check size={16} color="#059669" />
                      )}
                    </Pressable>
                  ))}
                </WebScrollView>
              </View>
            )}
            {showHomeDropdown && homeSearchQuery.trim() && filteredHomes.length === 0 && (
              <View style={styles.typeaheadDropdown}>
                <View style={styles.dropdownEmpty}>
                  <Text style={styles.dropdownEmptyText}>No homes found</Text>
                </View>
              </View>
            )}
          </View>
        </View>

        <View style={styles.formGroup}>
          <View style={styles.inputModeSelector}>
            <Pressable
              style={[
                styles.modeButton,
                inputMode === 'upload' && styles.modeButtonActive
              ]}
              onPress={() => {
                setInputMode('upload');
                setError(null);
              }}
            >
              <Upload size={16} color={inputMode === 'upload' ? '#ffffff' : '#6b7280'} />
              <Text style={[
                styles.modeButtonText,
                inputMode === 'upload' && styles.modeButtonTextActive
              ]}>
                Upload File
              </Text>
            </Pressable>
            <Pressable
              style={[
                styles.modeButton,
                inputMode === 'paste' && styles.modeButtonActive
              ]}
              onPress={() => {
                setInputMode('paste');
                setError(null);
              }}
            >
              <FileText size={16} color={inputMode === 'paste' ? '#ffffff' : '#6b7280'} />
              <Text style={[
                styles.modeButtonText,
                inputMode === 'paste' && styles.modeButtonTextActive
              ]}>
                Paste JSON
              </Text>
            </Pressable>
            <Pressable
              style={[
                styles.modeButton,
                inputMode === 'pdf' && styles.modeButtonActive
              ]}
              onPress={() => {
                setInputMode('pdf');
                setError(null);
              }}
            >
              <Upload size={16} color={inputMode === 'pdf' ? '#ffffff' : '#6b7280'} />
              <Text style={[
                styles.modeButtonText,
                inputMode === 'pdf' && styles.modeButtonTextActive
              ]}>
                Upload PDF
              </Text>
            </Pressable>
          </View>

          {inputMode === 'upload' ? (
            jsonFile ? (
              <View style={styles.fileSelected}>
                <View style={styles.fileSelectedHeader}>
                  <View style={styles.fileSelectedInfo}>
                    <FileText size={20} color="#10b981" />
                    <View>
                      <Text style={styles.fileName}>{jsonFile.name}</Text>
                      <Text style={styles.fileInfo}>
                        {jsonFile.data.property_records?.length || 0} records found
                      </Text>
                    </View>
                  </View>
                  <Pressable onPress={() => setJsonFile(null)}>
                    <X size={20} color="#6b7280" />
                  </Pressable>
                </View>
              </View>
            ) : (
              <Pressable
                style={styles.uploadButton}
                onPress={handlePickDocument}
              >
                <Upload size={20} color="#4b5563" />
                <Text style={styles.uploadButtonText}>Choose JSON File</Text>
              </Pressable>
            )
          ) : inputMode === 'paste' ? (
            <TextInput
              style={styles.jsonTextArea}
              value={jsonPaste}
              onChangeText={setJsonPaste}
              placeholder='Paste JSON here...'
              multiline
              numberOfLines={12}
              textAlignVertical="top"
            />
          ) : (
            pdfFile ? (
              <View style={styles.fileSelected}>
                <View style={styles.fileSelectedHeader}>
                  <View style={styles.fileSelectedInfo}>
                    <FileText size={20} color="#10b981" />
                    <View>
                      <Text style={styles.fileName}>{pdfFile.name}</Text>
                      <Text style={styles.fileInfo}>
                        Ready to extract records with AI
                      </Text>
                    </View>
                  </View>
                  <Pressable onPress={() => setPdfFile(null)}>
                    <X size={20} color="#6b7280" />
                  </Pressable>
                </View>
              </View>
            ) : (
              <Pressable
                style={styles.uploadButton}
                onPress={handlePickPDF}
              >
                <Upload size={20} color="#4b5563" />
                <Text style={styles.uploadButtonText}>Choose PDF File</Text>
              </Pressable>
            )
          )}
        </View>

        {error && (
          <View style={styles.errorContainer}>
            <AlertCircle size={16} color="#dc2626" />
            <Text style={styles.errorText}>{error}</Text>
          </View>
        )}

        {success && (
          <View style={styles.successContainer}>
            <Check size={16} color="#059669" />
            <Text style={styles.successText}>{success}</Text>
          </View>
        )}

        <Pressable
          style={[
            styles.parseButton,
            parseButtonDisabled && styles.parseButtonDisabled
          ]}
          onPress={inputMode === 'pdf' ? handleParsePDF : handleLoadJSON}
          disabled={parseButtonDisabled}
        >
          {loading ? (
            <ActivityIndicator size="small" color="#ffffff" />
          ) : (
            <>
              <FileText size={20} color="#ffffff" />
              <Text style={styles.parseButtonText}>{inputMode === 'pdf' ? 'Parse PDF' : 'Load Records'}</Text>
            </>
          )}
        </Pressable>
        {inputMode === 'pdf' && pdfParseStatus !== 'idle' && (
          <Text style={styles.parseStatusText}>
            {pdfParseStatus === 'extracting' ? 'Preparing PDF upload...' : 'Parsing property records with AI...'}
          </Text>
        )}
      </View>

      {/* Records Preview Section */}
      {records.length > 0 && (
        <View style={styles.card}>
          <View style={styles.cardHeader}>
            <Text style={styles.cardTitle}>Extracted Records</Text>
            <View style={styles.recordsSummary}>
              <Text style={styles.summaryText}>
                Total: {records.length} | Accepted: {acceptedCount} | Skipped: {skippedCount}
              </Text>
            </View>
          </View>

          {/* Bulk Actions */}
          <View style={styles.bulkActions}>
            <Pressable style={styles.bulkButton} onPress={handleAcceptAll}>
              <Check size={16} color="#059669" />
              <Text style={styles.bulkButtonText}>Accept All</Text>
            </Pressable>
            <Pressable style={styles.bulkButton} onPress={handleSkipAll}>
              <X size={16} color="#dc2626" />
              <Text style={styles.bulkButtonText}>Skip All</Text>
            </Pressable>
          </View>

          {/* Records List */}
          <WebScrollView style={styles.recordsList}>
            {records.map((record) => (
              <View
                key={record.tempId}
                style={[
                  styles.recordCard,
                  record.state === 'skipped' && styles.recordCardSkipped
                ]}
              >
                <View style={styles.recordHeader}>
                  <View style={[styles.recordTypeBadge, getRecordTypeBadgeStyle(record.record_type)]}>
                    <Text style={styles.recordTypeBadgeText}>
                      {record.record_type.replace('_', ' ')}
                    </Text>
                  </View>
                  <Text style={styles.recordDate}>{record.date}</Text>
                </View>

                <Text style={styles.recordTitle}>{record.title}</Text>
                {record.description && (
                  <Text style={styles.recordDescription} numberOfLines={2}>
                    {record.description}
                  </Text>
                )}
                {record.amount && (
                  <Text style={styles.recordAmount}>
                    ${record.amount.toLocaleString()}
                  </Text>
                )}

                <View style={styles.recordActions}>
                  <Pressable
                    style={styles.actionButton}
                    onPress={() => handleEditRecord(record)}
                  >
                    <Edit size={16} color="#4b5563" />
                    <Text style={styles.actionButtonText}>Edit</Text>
                  </Pressable>
                  
                  <Pressable
                    style={[
                      styles.actionButton,
                      record.state === 'skipped' ? styles.acceptButton : styles.skipButton
                    ]}
                    onPress={() => handleToggleRecordState(record.tempId)}
                  >
                    {record.state === 'skipped' ? (
                      <>
                        <Check size={16} color="#059669" />
                        <Text style={[styles.actionButtonText, styles.acceptButtonText]}>Accept</Text>
                      </>
                    ) : (
                      <>
                        <X size={16} color="#dc2626" />
                        <Text style={[styles.actionButtonText, styles.skipButtonText]}>Skip</Text>
                      </>
                    )}
                  </Pressable>
                </View>
              </View>
            ))}
          </WebScrollView>

          {/* Import Button */}
          {importProgress ? (
            <View style={styles.importProgress}>
              <ActivityIndicator size="small" color="#000000" />
              <Text style={styles.importProgressText}>
                Importing {importProgress.current} of {importProgress.total}...
              </Text>
            </View>
          ) : (
            <Pressable
              style={[
                styles.importButton,
                (acceptedCount === 0 || importing) && styles.importButtonDisabled
              ]}
              onPress={handleImportRecords}
              disabled={acceptedCount === 0 || importing}
            >
              <Check size={20} color="#ffffff" />
              <Text style={styles.importButtonText}>
                Import {acceptedCount} Record{acceptedCount !== 1 ? 's' : ''}
              </Text>
            </Pressable>
          )}
        </View>
      )}

      {/* Edit Modal */}
      <Modal
        visible={showEditModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowEditModal(false)}
      >
        <TouchableWithoutFeedback onPress={() => setShowEditModal(false)}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <ScrollView 
                style={styles.modalContent}
                contentContainerStyle={styles.modalContentContainer}
              >
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>Edit Property Record</Text>
                  <Pressable onPress={() => setShowEditModal(false)}>
                    <X size={24} color="#6b7280" />
                  </Pressable>
                </View>

                {editingRecord && (
                  <View style={styles.modalForm}>
                    <View style={styles.formGroup}>
                      <Text style={styles.label}>Record Type *</Text>
                      <View style={styles.pickerContainer}>
                        <Picker
                          selectedValue={editingRecord.record_type}
                          onValueChange={(value) =>
                            setEditingRecord({ ...editingRecord, record_type: value as any })
                          }
                          style={styles.picker}
                        >
                          <Picker.Item label="Permit" value="permit" />
                          <Picker.Item label="Tax Assessment" value="tax_assessment" />
                          <Picker.Item label="Sale" value="sale" />
                          <Picker.Item label="Lien" value="lien" />
                          <Picker.Item label="Other" value="other" />
                        </Picker>
                      </View>
                    </View>

                    <View style={styles.formGroup}>
                      <Text style={styles.label}>Title *</Text>
                      <TextInput
                        style={styles.input}
                        value={editingRecord.title}
                        onChangeText={(text) =>
                          setEditingRecord({ ...editingRecord, title: text })
                        }
                        placeholder="Enter title"
                      />
                    </View>

                    <View style={styles.formGroup}>
                      <Text style={styles.label}>Date *</Text>
                      <TextInput
                        style={styles.input}
                        value={editingRecord.date}
                        onChangeText={(text) =>
                          setEditingRecord({ ...editingRecord, date: text })
                        }
                        placeholder="YYYY-MM-DD"
                      />
                    </View>

                    <View style={styles.formGroup}>
                      <Text style={styles.label}>Amount</Text>
                      <TextInput
                        style={styles.input}
                        value={editingRecord.amount?.toString() || ''}
                        onChangeText={(text) =>
                          setEditingRecord({ 
                            ...editingRecord, 
                            amount: text ? parseFloat(text) : null 
                          })
                        }
                        placeholder="0.00"
                        keyboardType="decimal-pad"
                      />
                    </View>

                    <View style={styles.formGroup}>
                      <Text style={styles.label}>Description</Text>
                      <TextInput
                        style={[styles.input, styles.textArea]}
                        value={editingRecord.description || ''}
                        onChangeText={(text) =>
                          setEditingRecord({ ...editingRecord, description: text })
                        }
                        placeholder="Enter description"
                        multiline
                        numberOfLines={4}
                      />
                    </View>

                    <View style={styles.formGroup}>
                      <Text style={styles.label}>URL</Text>
                      <TextInput
                        style={styles.input}
                        value={editingRecord.url || ''}
                        onChangeText={(text) =>
                          setEditingRecord({ ...editingRecord, url: text })
                        }
                        placeholder="https://..."
                      />
                    </View>

                    <View style={styles.modalActions}>
                      <Pressable
                        style={styles.modalCancelButton}
                        onPress={() => setShowEditModal(false)}
                      >
                        <Text style={styles.modalCancelButtonText}>Cancel</Text>
                      </Pressable>
                      <Pressable
                        style={styles.modalSaveButton}
                        onPress={handleSaveEdit}
                      >
                        <Text style={styles.modalSaveButtonText}>Save Changes</Text>
                      </Pressable>
                    </View>
                  </View>
                )}
              </ScrollView>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    gap: 24,
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#f3f4f6',
    padding: 24,
    ...shadows.sm,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 24,
  },
  cardTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#000000',
  },
  recordsSummary: {
    marginLeft: 'auto',
  },
  summaryText: {
    fontSize: 14,
    color: '#6b7280',
  },
  formGroup: {
    marginBottom: 16,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 8,
  },
  pickerContainer: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    backgroundColor: '#ffffff',
    overflow: 'hidden',
  },
  picker: {
    height: Platform.OS === 'ios' ? 200 : 50,
  },
  typeaheadContainer: {
    position: 'relative',
    zIndex: 1000,
  },
  typeaheadContainerOpen: {
    zIndex: 10000,
  },
  typeaheadInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    backgroundColor: '#ffffff',
    paddingHorizontal: 12,
    paddingVertical: 10,
    gap: 8,
  },
  typeaheadInput: {
    flex: 1,
    fontSize: 14,
    color: '#111827',
    padding: 0,
  },
  clearButton: {
    padding: 4,
  },
  dropdownToggle: {
    padding: 4,
  },
  typeaheadDropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: 4,
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    ...shadows.md,
    maxHeight: 300,
    zIndex: 99999,
    elevation: Platform.OS === 'android' ? 10 : undefined,
  },
  dropdownScrollView: {
    maxHeight: 300,
  },
  dropdownItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  dropdownItemSelected: {
    backgroundColor: '#f0fdf4',
  },
  dropdownItemContent: {
    flex: 1,
  },
  dropdownItemText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
    marginBottom: 4,
  },
  dropdownItemMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  dropdownItemOwner: {
    fontSize: 12,
    color: '#6b7280',
  },
  dropdownItemId: {
    fontSize: 11,
    color: '#9ca3af',
    fontFamily: Platform.OS === 'web' ? 'monospace' : 'monospace',
  },
  dropdownEmpty: {
    paddingVertical: 16,
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  dropdownEmptyText: {
    fontSize: 14,
    color: '#6b7280',
  },
  uploadButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderWidth: 2,
    borderColor: '#d1d5db',
    borderStyle: 'dashed',
    borderRadius: 8,
    paddingVertical: 32,
    backgroundColor: '#f9fafb',
  },
  uploadButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#4b5563',
  },
  inputModeSelector: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 16,
  },
  modeButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    backgroundColor: '#ffffff',
  },
  modeButtonActive: {
    backgroundColor: '#000000',
    borderColor: '#000000',
  },
  modeButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6b7280',
  },
  modeButtonTextActive: {
    color: '#ffffff',
  },
  fileSelected: {
    padding: 16,
    backgroundColor: '#f0fdf4',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#86efac',
  },
  fileSelectedHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  fileSelectedInfo: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    flex: 1,
  },
  fileName: {
    fontSize: 14,
    fontWeight: '500',
    color: '#166534',
    marginBottom: 4,
  },
  fileInfo: {
    fontSize: 12,
    color: '#059669',
  },
  jsonTextArea: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    fontFamily: Platform.OS === 'web' ? 'monospace' : 'monospace',
    color: '#111827',
    backgroundColor: '#ffffff',
    minHeight: 200,
    maxHeight: 400,
  },
  parseButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#000000',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    marginTop: 8,
  },
  parseButtonDisabled: {
    opacity: 0.5,
  },
  parseButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#ffffff',
  },
  parseStatusText: {
    marginTop: 8,
    fontSize: 12,
    color: '#4b5563',
    textAlign: 'center',
  },
  errorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#fee2e2',
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
  },
  errorText: {
    flex: 1,
    color: '#dc2626',
    fontSize: 14,
  },
  successContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#d1fae5',
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
  },
  successText: {
    flex: 1,
    color: '#059669',
    fontSize: 14,
  },
  bulkActions: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
  },
  bulkButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#d1d5db',
    backgroundColor: '#ffffff',
  },
  bulkButtonText: {
    fontSize: 13,
    fontWeight: '500',
    color: '#374151',
  },
  recordsList: {
    maxHeight: 600,
  },
  recordCard: {
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    backgroundColor: '#ffffff',
    marginBottom: 12,
  },
  recordCardSkipped: {
    opacity: 0.5,
    backgroundColor: '#f9fafb',
  },
  recordHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  recordTypeBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  recordTypeBadgeText: {
    fontSize: 11,
    fontWeight: '600',
    textTransform: 'uppercase',
    color: '#ffffff',
  },
  permitBadge: {
    backgroundColor: '#3b82f6',
  },
  saleBadge: {
    backgroundColor: '#10b981',
  },
  taxBadge: {
    backgroundColor: '#f59e0b',
  },
  lienBadge: {
    backgroundColor: '#ef4444',
  },
  otherBadge: {
    backgroundColor: '#6b7280',
  },
  recordDate: {
    fontSize: 13,
    color: '#6b7280',
  },
  recordTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 4,
  },
  recordDescription: {
    fontSize: 14,
    color: '#6b7280',
    marginBottom: 8,
  },
  recordAmount: {
    fontSize: 14,
    fontWeight: '500',
    color: '#059669',
    marginBottom: 12,
  },
  recordActions: {
    flexDirection: 'row',
    gap: 8,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#d1d5db',
    backgroundColor: '#ffffff',
  },
  actionButtonText: {
    fontSize: 13,
    fontWeight: '500',
    color: '#4b5563',
  },
  skipButton: {
    borderColor: '#fecaca',
    backgroundColor: '#fef2f2',
  },
  skipButtonText: {
    color: '#dc2626',
  },
  acceptButton: {
    borderColor: '#bbf7d0',
    backgroundColor: '#f0fdf4',
  },
  acceptButtonText: {
    color: '#059669',
  },
  importButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#000000',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    marginTop: 16,
  },
  importButtonDisabled: {
    opacity: 0.5,
  },
  importButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#ffffff',
  },
  importProgress: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    paddingVertical: 16,
  },
  importProgressText: {
    fontSize: 14,
    color: '#6b7280',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  modalContent: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    maxWidth: 600,
    width: '100%',
    maxHeight: '90%',
  },
  modalContentContainer: {
    padding: 24,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
  },
  modalForm: {
    gap: 16,
  },
  input: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    color: '#111827',
    backgroundColor: '#ffffff',
  },
  textArea: {
    minHeight: 100,
    textAlignVertical: 'top',
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  modalCancelButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    backgroundColor: '#ffffff',
    alignItems: 'center',
  },
  modalCancelButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
  },
  modalSaveButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    backgroundColor: '#000000',
    alignItems: 'center',
  },
  modalSaveButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#ffffff',
  },
});

export default PropertyImport;

